import React, { useCallback, useEffect, useState } from 'react';

import { apiPatch } from '../../../lib/api_v2';
import db from '../../../lib/db';
import { logError } from '../../../lib/logger';
import { useRouteWorkspace } from '../../../contexts/RouteWorkspaceContext';

import BrowserPushToggle from '../account/BrowserPushToggle';
import { SettingsGroup, SettingsRow, SettingsToggle } from '../../shared/SettingsList';

// only_mentions is the profile enum default. Exported so summary rows (e.g.
// WorkspaceOptionsModal) can derive their labels from the same table.
export const DEFAULT_NOTIFICATION_PREFERENCE = 'only_mentions';
export const WIDGET_ANNOUNCEMENTS_PREFERENCE = 'widget_announcements_enabled';
export const WORKSPACE_NOTIFICATION_PREFERENCES = [
  { value: 'all_notifications', label: 'All messages' },
  { value: 'only_mentions', label: 'Only @ mentions' },
  { value: 'no_notifications', label: 'None' },
];

export const notificationPreferenceLabel = (preference) =>
  (
    WORKSPACE_NOTIFICATION_PREFERENCES.find((option) => option.value === preference) ||
    WORKSPACE_NOTIFICATION_PREFERENCES.find((option) => option.value === DEFAULT_NOTIFICATION_PREFERENCE)
  ).label;

const normalizeNotificationPreference = (preference) =>
  WORKSPACE_NOTIFICATION_PREFERENCES.some((option) => option.value === preference) ? preference : DEFAULT_NOTIFICATION_PREFERENCE;

const NotificationPreferenceRow = ({ option, selected, disabled, onSelect }) => {
  const handleClick = useCallback(
    function handleNotificationPreferenceRowClick() {
      onSelect(option.value);
    },
    [onSelect, option.value],
  );

  return <SettingsRow label={option.label} selected={selected} disabled={disabled} onClick={handleClick} />;
};

const NotificationsSettings = () => {
  const { workspaceId, profileId, profile } = useRouteWorkspace();
  const profilePreference = profile?.notification_preference || DEFAULT_NOTIFICATION_PREFERENCE;
  const normalizedProfilePreference = normalizeNotificationPreference(profilePreference);
  const profileReceivesWidgetAnnouncements = profile?.preferences?.[WIDGET_ANNOUNCEMENTS_PREFERENCE] === true;
  const [selectedPreference, setSelectedPreference] = useState(normalizedProfilePreference);
  const [receivesWidgetAnnouncements, setReceivesWidgetAnnouncements] = useState(profileReceivesWidgetAnnouncements);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSelectedPreference(normalizedProfilePreference);
    setReceivesWidgetAnnouncements(profileReceivesWidgetAnnouncements);
    setError(null);
  }, [normalizedProfilePreference, profileId, profileReceivesWidgetAnnouncements]);

  const isDisabled = isSaving || !workspaceId || !profileId;

  const saveProfileSettings = useCallback(
    async (attributes) => {
      const response = await apiPatch(`/profiles/${profileId}`, { profile: attributes });
      const data = await response.json();
      const updatedProfile = data?.data?.profile;

      if (!response.ok || data.ok !== true || !updatedProfile) throw new Error('Unable to save notification settings.');

      db.profiles.put(updatedProfile).catch((cacheError) => {
        logError('NotificationsSettings', 'Failed to cache updated profile', { error: cacheError });
      });
    },
    [profileId],
  );

  const handleSelectPreference = useCallback(
    async (nextPreference) => {
      if (isDisabled || nextPreference === selectedPreference) return;

      const previousPreference = selectedPreference;
      setSelectedPreference(nextPreference);
      setError(null);
      setIsSaving(true);

      try {
        await saveProfileSettings({ notification_preference: nextPreference });
      } catch (saveError) {
        setSelectedPreference(previousPreference);
        setError('Unable to save notification settings.');
        logError('NotificationsSettings', 'Failed to save notification preference', { error: saveError });
      } finally {
        setIsSaving(false);
      }
    },
    [isDisabled, saveProfileSettings, selectedPreference],
  );

  const handleWidgetAnnouncementsChange = useCallback(
    async (nextValue) => {
      if (isDisabled || nextValue === receivesWidgetAnnouncements) return;

      const previousValue = receivesWidgetAnnouncements;
      setReceivesWidgetAnnouncements(nextValue);
      setError(null);
      setIsSaving(true);

      try {
        await saveProfileSettings({ preferences: { [WIDGET_ANNOUNCEMENTS_PREFERENCE]: nextValue } });
      } catch (saveError) {
        setReceivesWidgetAnnouncements(previousValue);
        setError('Unable to save notification settings.');
        logError('NotificationsSettings', 'Failed to save widget announcement preference', { error: saveError });
      } finally {
        setIsSaving(false);
      }
    },
    [isDisabled, receivesWidgetAnnouncements, saveProfileSettings],
  );

  if (!profile) return null;

  return (
    <div className="w-full pt-10">
      <SettingsGroup>
        {WORKSPACE_NOTIFICATION_PREFERENCES.map((option) => (
          <NotificationPreferenceRow
            key={option.value}
            option={option}
            selected={selectedPreference === option.value}
            disabled={isDisabled}
            onSelect={handleSelectPreference}
          />
        ))}
      </SettingsGroup>

      <SettingsGroup className="mt-3">
        <SettingsToggle
          label="Receive widget announcements"
          checked={receivesWidgetAnnouncements}
          disabled={isDisabled}
          onChange={handleWidgetAnnouncementsChange}
        />
      </SettingsGroup>

      <BrowserPushToggle />

      <div className="mt-3 min-h-5 text-xs" aria-live="polite">
        {error ? <span className="text-red-700 dark:text-red-300">{error}</span> : null}
      </div>
    </div>
  );
};

export default NotificationsSettings;
